// @ts-check

/**
 * Represents a value that may be of type T, or null.
 *
 * @template T
 * @typedef {T | null} Nullable
 */

/**
 * Stable identifier for error codes used in the `files-dropzone-error` event.
 *
 * @typedef {typeof FilesDropzone.ERROR_CODES[keyof typeof FilesDropzone.ERROR_CODES]} FilesDropzoneErrorCode
 */

/**
 * The detail payload for the `files-dropzone-error` event.
 *
 * @typedef {Object} FilesDropzoneErrorDetail
 * @property {FilesDropzoneErrorCode} code - Stable identifier for the type of error that occurred.
 * @property {unknown} error - The underlying error object or value that triggered the event.
 */

/**
 * Stable identifier for rejection codes used in the various drop-related events.
 *
 * @typedef {typeof FilesDropzone.REJECTION_CODES[keyof typeof FilesDropzone.REJECTION_CODES]} FilesDropzoneRejectionCode
 */

import { isValidFile } from './utils/is-valid-file.js';
import { getFilesFromEvent } from './utils/files-selector.js';

const css = String.raw;
const html = String.raw;
const COMPONENT_NAME = 'files-dropzone';
const template = document.createElement('template');

const styles = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  :host([hidden]),
  [hidden] {
    display: none !important;
  }

  :host {
    --dropzone-border-width: 2px;
    --dropzone-border-style: dashed;
    --dropzone-border-radius: 0.25rem;
    --dropzone-border-color: #6c757d;
    --dropzone-border-color-dragover: #0d6efd;
    --dropzone-border-color-hover: var(--dropzone-border-color-dragover);
    --dropzone-background-color: #ffffff;
    --dropzone-background-color-dragover: #f4f4f5;
    --dropzone-background-color-hover: var(--dropzone-background-color-dragover);
    --dropzone-body-color: #3f3f46;
    --dropzone-body-color-dragover: var(--dropzone-body-color);
    --dropzone-body-color-hover: var(--dropzone-body-color-dragover);
    --dropzone-focus-shadow-rgb: 49, 132, 253;
    --dropzone-focus-box-shadow: 0 0 0 0.25rem rgba(var(--dropzone-focus-shadow-rgb), 0.5);
    --transition-duration: 0.2s; /* for backwards compatibility */
    --dropzone-transition-duration: var(--transition-duration);

    display: block;
  }

  :host(:not([no-style])) .dropzone {
    border: var(--dropzone-border-width) var(--dropzone-border-style) var(--dropzone-border-color);
    border-radius: var(--dropzone-border-radius);
    padding: 3rem 1rem;
    overflow: hidden;
    background-color: var(--dropzone-background-color);
    color: var(--dropzone-body-color);
    text-align: center;
    cursor: pointer;
    transition:
      border var(--dropzone-transition-duration) ease-in-out,
      background-color var(--dropzone-transition-duration) ease-in-out,
      color var(--dropzone-transition-duration) ease-in-out,
      box-shadow var(--dropzone-transition-duration) ease-in-out;
  }

  :host(:not([no-style])[disabled]) .dropzone {
    opacity: 0.8;
    cursor: not-allowed;
    user-select: none;
  }

  :host(:not([no-style]):not([disabled])) .dropzone--dragover {
    border-color: var(--dropzone-border-color-dragover);
    background-color: var(--dropzone-background-color-dragover);
    color: var(--dropzone-body-color-dragover);
  }

  :host(:not([no-style]):not([disabled])) .dropzone:focus-visible {
    outline: none;
    box-shadow: var(--dropzone-focus-box-shadow);
  }

  @media (hover: hover) {
    :host(:not([no-style]):not([disabled])) .dropzone:not(.dropzone--dragover):hover {
      border-color: var(--dropzone-border-color-hover);
      background-color: var(--dropzone-background-color-hover);
      color: var(--dropzone-body-color-hover);
    }
  }
`;

template.innerHTML = html`
  <style>
    ${styles}
  </style>

  <input type="file" id="file-input" hidden />

  <div part="dropzone" class="dropzone" id="dropzone" tabindex="0" role="button" aria-disabled="false">
    <slot><span>Drag 'n' drop files here, or click to select files</span></slot>
  </div>
`;

/**
 * @summary A custom element that allows users to drag and drop files into it.
 * @documentation https://github.com/georapbox/files-dropzone-element
 *
 * @tagname files-dropzone - This is the default tag name, unless overridden by the `defineCustomElement` method.
 *
 * @property {string} accept - A comma-separated list of unique file type specifiers describing file types to allow.
 * @property {boolean} disabled - Determines whether the dropzone is disabled.
 * @property {number} maxFiles - The maximum number of files allowed to be dropped.
 * @property {number} maxSize - The maximum file size allowed in bytes.
 * @property {number} minSize - The minimum file size allowed in bytes.
 * @property {boolean} multiple - Allows multiple files to be dropped.
 * @property {boolean} autoFocus - Automatically focuses the dropzone when it's connected to the DOM.
 * @property {boolean} noStyle - Prevents the dropzone from applying any styling.
 *
 * @attribute {string} accept - A comma-separated list of unique file type specifiers describing file types to allow.
 * @attribute {boolean} disabled - Determines whether the dropzone is disabled.
 * @attribute {number} max-files - The maximum number of files allowed to be dropped.
 * @attribute {number} max-size - The maximum file size allowed in bytes.
 * @attribute {number} min-size - The minimum file size allowed in bytes.
 * @attribute {boolean} multiple - Allows multiple files to be dropped.
 * @attribute {boolean} auto-focus - Automatically focuses the dropzone when it's connected to the DOM.
 * @attribute {boolean} no-style - Prevents the dropzone from applying any styling.
 *
 * @slot - The default slot content of the dropzone.
 *
 * @csspart dropzone - The dropzone element.
 * @csspart dropzone--dragover - The state of the dropzone when dragging over it.
 *
 * @cssproperty --dropzone-border-width - The border width of the dropzone.
 * @cssproperty --dropzone-border-style - The border style of the dropzone.
 * @cssproperty --dropzone-border-radius - The border radius of the dropzone.
 * @cssproperty --dropzone-border-color - The border color of the dropzone.
 * @cssproperty --dropzone-border-color-dragover - The border color of the dropzone when dragging over it.
 * @cssproperty --dropzone-border-color-hover - The border color of the dropzone when hovering over it.
 * @cssproperty --dropzone-background-color - The background color of the dropzone.
 * @cssproperty --dropzone-background-color-dragover - The background color of the dropzone when dragging over it.
 * @cssproperty --dropzone-background-color-hover - The background color of the dropzone when hovering over it.
 * @cssproperty --dropzone-body-color - The text color of the dropzone.
 * @cssproperty --dropzone-body-color-dragover - The text color of the dropzone when dragging over it.
 * @cssproperty --dropzone-body-color-hover - The text color of the dropzone when hovering over it.
 * @cssproperty --dropzone-focus-shadow-rgb - The RGB value of the dropzone's focus shadow.
 * @cssproperty --dropzone-focus-box-shadow - The box shadow of the dropzone when focused.
 * @cssproperty --dropzone-transition-duration - The transition's duration for the dropzone area.
 *
 * @event files-dropzone-drop - Fired when files are dropped.
 * @event files-dropzone-drop-accepted - Fired when files dropped files are accepted.
 * @event files-dropzone-drop-rejected - Fired when files dropped files are rejected.
 * @event files-dropzone-dragenter - Fired when files are dragged into the dropzone.
 * @event files-dropzone-dragover - Fired when files are dragged over the dropzone.
 * @event files-dropzone-dragleave - Fired when files are dragged out of the dropzone.
 * @event files-dropzone-error - Fired when there is any error in the process of reading dropped files or directories.
 *
 * @method defineCustomElement - Static method. Defines a custom element with the given name.
 * @method openFileDialog - Instance method. Opens the file dialog programmatically.
 */
class FilesDropzone extends HTMLElement {
  /**
   * Central list of error codes used in drop error event.
   * @readonly
   */
  static ERROR_CODES = /** @type {const} */ ({
    FILE_DIALOG_OPEN_FAILED: 'FILE_DIALOG_OPEN_FAILED',
    FILE_INPUT_CHANGE_FAILED: 'FILE_INPUT_CHANGE_FAILED',
    DROP_EVENT_PROCESSING_FAILED: 'DROP_EVENT_PROCESSING_FAILED',
    UNKNOWN_ERROR: 'UNKNOWN_ERROR'
  });

  /**
   * Central list of rejection codes for files that are rejected during the drop process.
   * @readonly
   */
  static REJECTION_CODES = /** @type {const} */ ({
    TOO_MANY_FILES: 'TOO_MANY_FILES',
    FILE_TOO_LARGE: 'FILE_TOO_LARGE',
    FILE_TOO_SMALL: 'FILE_TOO_SMALL',
    INVALID_MIME_TYPE: 'INVALID_MIME_TYPE'
  });

  /** @type {Nullable<HTMLInputElement>} */
  #fileInput = null;

  /** @type {Nullable<HTMLElement>} */
  #dropzoneEl = null;

  constructor() {
    super();

    if (!this.shadowRoot) {
      const shadowRoot = this.attachShadow({ mode: 'open', delegatesFocus: true });
      shadowRoot.appendChild(template.content.cloneNode(true));
    }

    if (this.shadowRoot) {
      this.#fileInput = /** @type {Nullable<HTMLInputElement>} */ (this.shadowRoot.getElementById('file-input'));
      this.#dropzoneEl = this.shadowRoot.getElementById('dropzone');
    }
  }

  static get observedAttributes() {
    return ['accept', 'disabled', 'multiple'];
  }

  /**
   * Lifecycle method that is called when attributes are changed, added, removed, or replaced.
   *
   * @param {string} name - The name of the attribute.
   * @param {string} oldValue - The old value of the attribute.
   * @param {string} newValue - The new value of the attribute.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'accept' && oldValue !== newValue && this.#fileInput) {
      this.#fileInput.accept = this.accept;
    }

    if (name === 'disabled' && oldValue !== newValue && this.#fileInput) {
      this.#fileInput.disabled = this.disabled;

      if (this.disabled) {
        this.#dropzoneEl?.removeAttribute('tabindex');
        this.#dropzoneEl?.setAttribute('aria-disabled', 'true');
      } else {
        this.#dropzoneEl?.setAttribute('tabindex', '0');
        this.#dropzoneEl?.setAttribute('aria-disabled', 'false');
      }
    }

    if (name === 'multiple' && oldValue !== newValue && this.#fileInput) {
      this.#fileInput.multiple = this.multiple;
    }
  }

  /**
   * Lifecycle method that is called when the element is added to the DOM.
   */
  connectedCallback() {
    this.#upgradeProperty('accept');
    this.#upgradeProperty('disabled');
    this.#upgradeProperty('maxFiles');
    this.#upgradeProperty('maxSize');
    this.#upgradeProperty('minSize');
    this.#upgradeProperty('multiple');
    this.#upgradeProperty('autoFocus');
    this.#upgradeProperty('noStyle');

    this.#fileInput?.addEventListener('change', this.#handleFileInputChange);
    this.#dropzoneEl?.addEventListener('dragenter', this.#handleDragEnter);
    this.#dropzoneEl?.addEventListener('dragover', this.#handleDragOver);
    this.#dropzoneEl?.addEventListener('dragleave', this.#handleDragLeave);
    this.#dropzoneEl?.addEventListener('drop', this.#handleDrop);
    this.#dropzoneEl?.addEventListener('click', this.#handleClick);
    this.#dropzoneEl?.addEventListener('keyup', this.#handleKeyUp);

    this.autoFocus && this.#dropzoneEl?.focus();
  }

  /**
   * Lifecycle method that is called when the element is removed from the DOM.
   */
  disconnectedCallback() {
    this.#fileInput?.removeEventListener('change', this.#handleFileInputChange);
    this.#dropzoneEl?.removeEventListener('dragenter', this.#handleDragEnter);
    this.#dropzoneEl?.removeEventListener('dragover', this.#handleDragOver);
    this.#dropzoneEl?.removeEventListener('dragleave', this.#handleDragLeave);
    this.#dropzoneEl?.removeEventListener('drop', this.#handleDrop);
    this.#dropzoneEl?.removeEventListener('click', this.#handleClick);
    this.#dropzoneEl?.removeEventListener('keyup', this.#handleKeyUp);
  }

  /**
   * @type {string} - A comma-separated list of unique file type specifiers describing file types to allow.
   * @attribute accept
   * @default ''
   */
  get accept() {
    return this.getAttribute('accept') || '';
  }

  set accept(value) {
    this.setAttribute('accept', value != null ? value.toString() : value);
  }

  /**
   * @type {boolean} - Determines whether the dropzone is disabled.
   * @attribute disabled
   * @default false
   */
  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(value) {
    this.toggleAttribute('disabled', !!value);
  }

  /**
   * @type {number} - The maximum number of files allowed to be dropped.
   * @attribute max-files
   * @default Infinity
   */
  get maxFiles() {
    const num = Number(this.getAttribute('max-files')) || 0;

    if (num <= 0) {
      return Infinity;
    }

    return Math.floor(Math.abs(num));
  }

  set maxFiles(value) {
    this.setAttribute('max-files', value != null ? value.toString() : value);
  }

  /**
   * @type {number} - The maximum file size allowed in bytes.
   * @attribute max-size
   * @default Infinity
   */
  get maxSize() {
    const value = this.getAttribute('max-size');

    if (value === null) {
      return Infinity;
    }

    const num = Number(value);

    return Number.isNaN(num) ? Infinity : num;
  }

  set maxSize(value) {
    this.setAttribute('max-size', value != null ? value.toString() : value);
  }

  /**
   * @type {number} - The minimum file size allowed in bytes.
   * @attribute min-size
   * @default 0
   */
  get minSize() {
    const value = this.getAttribute('min-size');

    if (value === null) {
      return 0;
    }

    const num = Number(value);

    return Number.isNaN(num) ? 0 : num;
  }

  set minSize(value) {
    this.setAttribute('min-size', value != null ? value.toString() : value);
  }

  /**
   * @type {boolean} - Allows multiple files to be dropped.
   * @attribute multiple
   * @default false
   */
  get multiple() {
    return this.hasAttribute('multiple');
  }

  set multiple(value) {
    this.toggleAttribute('multiple', !!value);
  }

  /**
   * @type {boolean} - Automatically focuses the dropzone when it's connected to the DOM.
   * @attribute auto-focus
   * @default false
   */
  get autoFocus() {
    return this.hasAttribute('auto-focus');
  }

  set autoFocus(value) {
    this.toggleAttribute('auto-focus', !!value);
  }

  /**
   * @type {boolean} - Prevents the dropzone from applying any styling.
   * @attribute no-style
   * @default false
   */
  get noStyle() {
    return this.hasAttribute('no-style');
  }

  set noStyle(value) {
    this.toggleAttribute('no-style', !!value);
  }

  /**
   * Emit a custom event with the given name and detail.
   *
   * @template D
   * @param {string} name - The name of the event.
   * @param {D} [detail] - The detail payload of the event.
   * @param {CustomEventInit<D>} [init] - Override the default event initialization options.
   * @returns {boolean} - Returns false if at least one event listener called `preventDefault()`, otherwise true.
   */
  #emitEvent(name, detail, init) {
    const options = {
      bubbles: true,
      composed: true,
      cancelable: false,
      ...init,
      detail
    };
    const evt = new CustomEvent(`${COMPONENT_NAME}-${name}`, options);
    return this.dispatchEvent(evt);
  }

  /**
   * Emit a standardized error event with typed detail.
   *
   * @param {FilesDropzoneErrorCode} code - Stable identifier for the type of error that occurred.
   * @param {unknown} error - The underlying error that triggered the event.
   */
  #emitErrorEvent(code, error) {
    /** @type {FilesDropzoneErrorDetail} */
    const detail = { code, error };

    this.#emitEvent('error', detail);
  }

  /**
   * Handles the change event of the file input.
   *
   * @param {*} evt - The event object.
   */
  #handleFileInputChange = async evt => {
    try {
      this.#handleFilesSelect(await getFilesFromEvent(evt));
    } catch (err) {
      this.#emitErrorEvent(FilesDropzone.ERROR_CODES.FILE_INPUT_CHANGE_FAILED, err);
    }
  };

  /**
   * Handles the dragenter event of the dropzone.
   */
  #handleDragEnter = () => {
    if (this.disabled) {
      return;
    }

    this.#emitEvent('dragenter');
  };

  /**
   * Handles the dragover event of the dropzone.
   *
   * @param {*} evt - The event object.
   */
  #handleDragOver = evt => {
    evt.preventDefault();

    if (this.disabled) {
      evt.dataTransfer.dropEffect = 'none';
      return;
    }

    evt.dataTransfer.dropEffect = 'copy';

    if (this.#dropzoneEl) {
      this.#dropzoneEl.classList.add('dropzone--dragover');
      this.#dropzoneEl.part.add('dropzone--dragover');
    }

    this.#emitEvent('dragover');
  };

  /**
   * Handles the dragleave event of the dropzone.
   */
  #handleDragLeave = () => {
    if (this.disabled) {
      return;
    }

    if (this.#dropzoneEl) {
      this.#dropzoneEl.classList.remove('dropzone--dragover');
      this.#dropzoneEl.part.remove('dropzone--dragover');
    }

    this.#emitEvent('dragleave');
  };

  /**
   * Handles the drop event of the dropzone.
   *
   * @param {*} evt - The event object.
   */
  #handleDrop = async evt => {
    if (this.disabled) {
      return;
    }

    evt.preventDefault();

    if (this.#dropzoneEl) {
      this.#dropzoneEl.classList.remove('dropzone--dragover');
      this.#dropzoneEl.part.remove('dropzone--dragover');
    }

    try {
      this.#handleFilesSelect(await getFilesFromEvent(evt));
    } catch (err) {
      this.#emitErrorEvent(FilesDropzone.ERROR_CODES.DROP_EVENT_PROCESSING_FAILED, err);
    }
  };

  /**
   * Handles the click event of the dropzone.
   */
  #handleClick = () => {
    if (this.disabled) {
      return;
    }

    this.openFileDialog();
  };

  /**
   * Handles the keyup event of the dropzone.
   *
   * @param {*} evt - The event object.
   */
  #handleKeyUp = evt => {
    if (this.disabled) {
      return;
    }

    if (evt.key === ' ' || evt.key === 'Enter') {
      this.openFileDialog();
    }
  };

  /**
   * Handles the selection of files.
   *
   * @param {File[]} files - The files to handle.
   */
  #handleFilesSelect(files) {
    if (!Array.isArray(files) || !files.length) {
      return;
    }

    const acceptedFiles = [];
    const rejectedFiles = [];
    const filesLength = files.length;

    // If the component is not in multiple mode, reject all files.
    if (!this.multiple && filesLength > 1) {
      for (const file of files) {
        rejectedFiles.push({
          file,
          errors: [
            {
              code: FilesDropzone.REJECTION_CODES.TOO_MANY_FILES,
              message: `Too many files selected. Only 1 file is allowed.`
            }
          ]
        });
      }
    } else if (this.multiple && filesLength > this.maxFiles) {
      // If the component is in multiple mode, but the number of files exceeds
      // the maxFiles attribute, reject all files.
      for (const file of files) {
        rejectedFiles.push({
          file,
          errors: [
            {
              code: FilesDropzone.REJECTION_CODES.TOO_MANY_FILES,
              message: `Too many files selected. Only ${this.maxFiles} ${this.maxFiles > 1 ? 'files are' : 'file is'} allowed.`
            }
          ]
        });
      }
    } else {
      // Validate each file. If it's valid, add it to the accepted files array,
      // otherwise add it to the rejected files array.
      for (const file of files) {
        const fileHasValidType = isValidFile(file, this.accept);
        const fileExceedsMaxSize = file.size > this.maxSize;
        const fileIsSmallerThanMinSize = file.size < this.minSize;

        if (fileHasValidType && !fileExceedsMaxSize && !fileIsSmallerThanMinSize) {
          acceptedFiles.push(file);
        } else {
          const errors = [];

          if (!fileHasValidType) {
            errors.push({
              code: FilesDropzone.REJECTION_CODES.INVALID_MIME_TYPE,
              message: `File type "${file.type}" is not accepted.`
            });
          }

          if (fileExceedsMaxSize) {
            errors.push({
              code: FilesDropzone.REJECTION_CODES.FILE_TOO_LARGE,
              message: `File size ${file.size} exceeds the maximum size of ${this.maxSize}.`
            });
          }

          if (fileIsSmallerThanMinSize) {
            errors.push({
              code: FilesDropzone.REJECTION_CODES.FILE_TOO_SMALL,
              message: `File size ${file.size} is smaller than the minimum size of ${this.minSize}.`
            });
          }

          rejectedFiles.push({ file, errors });
        }
      }
    }

    this.#emitEvent('drop', { acceptedFiles, rejectedFiles });

    if (acceptedFiles.length > 0) {
      this.#emitEvent('drop-accepted', { acceptedFiles });
    }

    if (rejectedFiles.length > 0) {
      this.#emitEvent('drop-rejected', { rejectedFiles });
    }

    if (this.#fileInput) {
      this.#fileInput.value = this.#fileInput.defaultValue;
    }
  }

  /**
   * Opens the file dialog programmatically.
   */
  openFileDialog() {
    if (this.disabled || !this.#fileInput) {
      return;
    }

    // In modern browsers, use the showPicker() method if available.
    // This way we are able to detect if the file dialog was blocked by the browser
    // (e.g. if the method was called outside a user gesture event).
    if ('showPicker' in HTMLInputElement.prototype && typeof this.#fileInput.showPicker === 'function') {
      try {
        this.#fileInput.showPicker();
      } catch (err) {
        this.#emitErrorEvent(FilesDropzone.ERROR_CODES.FILE_DIALOG_OPEN_FAILED, err);
      }
      return;
    }

    // Fallback for browsers that do not support showPicker().
    // This may not work if the method is called outside a user gesture event.
    // In such cases, the browser may block the file dialog from opening but
    // most likely it won't throw an error rather just a warning in the console.
    this.#fileInput.click();
  }

  /**
   * This is to safe guard against cases where, for instance, a framework may have added the element to the page and set a
   * value on one of its properties, but lazy loaded its definition. Without this guard, the upgraded element would miss that
   * property and the instance property would prevent the class property setter from ever being called.
   *
   * https://developers.google.com/web/fundamentals/web-components/best-practices#lazy-properties
   *
   * @param {'accept' | 'disabled' | 'maxFiles' | 'maxSize' | 'minSize' | 'multiple' | 'autoFocus' | 'noStyle'} prop - The property name to upgrade.
   */
  #upgradeProperty(prop) {
    /** @type {any} */
    const instance = this;

    if (Object.prototype.hasOwnProperty.call(instance, prop)) {
      const value = instance[prop];
      delete instance[prop];
      instance[prop] = value;
    }
  }

  /**
   * Defines a custom element with the given name.
   * The name must contain a dash (-).
   *
   * @param {string} [elementName='files-dropzone'] - The name of the custom element.
   * @example
   *
   * FilesDropzone.defineCustomElement('my-dropzone');
   */
  static defineCustomElement(elementName = COMPONENT_NAME) {
    if (typeof window !== 'undefined' && !window.customElements.get(elementName)) {
      window.customElements.define(elementName, FilesDropzone);
    }
  }
}

export { FilesDropzone };
